import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar, Gauge, DollarSign, MapPin, Wrench, CheckCircle } from 'lucide-react';

const SERVICE_TYPES = [
  { value: 'oil_change', label: 'Oil Change' },
  { value: 'tires', label: 'Tires' },
  { value: 'brakes', label: 'Brakes' },
  { value: 'battery', label: 'Battery' },
  { value: 'windshield_wipers', label: 'Windshield Wipers' },
  { value: 'air_filter', label: 'Air Filter' },
  { value: 'tire_rotation', label: 'Tire Rotation' },
  { value: 'brake_pads', label: 'Brake Pads' },
  { value: 'cabin_filter', label: 'Cabin Filter' },
  { value: 'transmission', label: 'Transmission Service' },
  { value: 'coolant', label: 'Coolant Flush' },
  { value: 'spark_plugs', label: 'Spark Plugs' },
  { value: 'other', label: 'Other Service' },
];

const LogMaintenanceDialog = ({ 
  open, 
  onClose, 
  item, 
  currentMileage,
  maintenanceItems,
  onSubmit 
}) => {
  const [formData, setFormData] = useState({
    service_type: item?.type || '',
    date: new Date().toISOString().split('T')[0],
    mileage: currentMileage || '',
    cost: '',
    vendor_name: '',
    is_diy: false,
    notes: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!item && !formData.service_type) {
      return;
    }

    const submitData = {
      ...formData,
      service_type: item?.type || formData.service_type,
      mileage: formData.mileage ? parseInt(formData.mileage) : null,
      cost: formData.cost ? parseFloat(formData.cost) : null,
    };
    
    onSubmit(submitData);
  };

  const dialogTitle = item?.name 
    ? `Log ${item.name}` 
    : 'Log Service';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <DialogTitle className="text-xl">{dialogTitle}</DialogTitle>
              <p className="text-sm text-slate-500">Record completed maintenance</p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 py-4">
          {/* Service Type Selector (only if no item pre-selected) */}
          {!item && (
            <div>
              <Label htmlFor="service_type" className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-teal-600" />
                Service Type
              </Label>
              <Select
                value={formData.service_type}
                onValueChange={(value) => handleChange('service_type', value)}
              >
                <SelectTrigger className="mt-1" data-testid="service-type-select">
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Date and Mileage */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-teal-600" />
                Service Date
              </Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                className="mt-1"
                required
                data-testid="service-date-input"
              />
            </div>
            <div>
              <Label htmlFor="mileage" className="flex items-center gap-2">
                <Gauge className="h-4 w-4 text-teal-600" />
                Mileage
              </Label>
              <Input
                id="mileage"
                type="number"
                placeholder={currentMileage ? `Current: ${currentMileage.toLocaleString()}` : 'Enter mileage'}
                value={formData.mileage}
                onChange={(e) => handleChange('mileage', e.target.value)}
                className="mt-1"
                data-testid="service-mileage-input"
              />
            </div>
          </div>

          {/* Cost */}
          <div>
            <Label htmlFor="cost" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-teal-600" />
              Cost
            </Label>
            <Input
              id="cost"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={formData.cost}
              onChange={(e) => handleChange('cost', e.target.value)}
              className="mt-1"
              data-testid="service-cost-input"
            />
          </div>

          {/* DIY Toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
            <div className="flex items-center gap-2">
              <Wrench className="h-4 w-4 text-slate-500" />
              <div>
                <Label htmlFor="is_diy" className="font-medium">DIY Service</Label>
                <p className="text-xs text-slate-500">I did this myself</p>
              </div>
            </div>
            <Switch
              id="is_diy"
              checked={formData.is_diy}
              onCheckedChange={(checked) => handleChange('is_diy', checked)}
              data-testid="diy-toggle"
            />
          </div>

          {/* Vendor (if not DIY) */}
          {!formData.is_diy && (
            <div>
              <Label htmlFor="vendor_name" className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-teal-600" />
                Service Provider
              </Label>
              <Input
                id="vendor_name"
                type="text"
                placeholder="Shop or mechanic name"
                value={formData.vendor_name}
                onChange={(e) => handleChange('vendor_name', e.target.value)}
                className="mt-1"
                data-testid="vendor-name-input"
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Any additional details about the service..."
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              className="mt-1"
              rows={3}
              data-testid="service-notes-input"
            />
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            onClick={handleSubmit}
            className="bg-green-600 hover:bg-green-700"
            data-testid="submit-log-btn"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Log Service
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LogMaintenanceDialog;
